import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Mic, Square, Check } from 'lucide-react'
import { checkVoiceLink, uploadRecording, VoiceLinkError } from '@/lib/api'
import { useWavRecorder } from '@/components/useWavRecorder'
import { micFailureMessage } from '@/lib/microphone'

/**
 * The record page (Build 1: voice capture for the gap queue).
 *
 * The owner gets a text, taps the link, speaks for twenty seconds, and reads
 * back what he said. That is the whole page. He is standing in a van or on a
 * roof, on a phone, possibly on one bar of signal — so it does one thing, with a
 * button big enough to hit without looking, and it never leaves him guessing
 * about whether it worked.
 *
 * NO LOGIN. The link token is the authentication (see workers/voice.ts). This
 * route is therefore Clerk-free by construction, exactly like the public chat:
 * it loads no auth JS and makes no token calls.
 *
 * ==== THE MICROPHONE AND THE FORMAT ====
 * Captured by src/components/useWavRecorder.ts and encoded by src/lib/wav.ts,
 * shared with Kelvo's Questions page since Build 10b (moved, not copied). Why
 * ScriptProcessorNode and not AudioWorklet, and why 16kHz PCM WAV resampled in
 * software, is recorded there — every word of it learned on this page first.
 *
 * ==== THE HEADER THIS PAGE DEPENDS ON ====
 * public/_headers grants `microphone=(self)` on /record/* ONLY. Without it
 * getUserMedia throws NotAllowedError before any prompt appears, and no browser
 * setting can override it. Verified in production 27 Aug 2026 — see the comment
 * block in that file.
 */

type Phase =
  /** Checking the link before we show anything. */
  | { kind: 'checking' }
  /** The link is bad; nothing to do but explain why. */
  | { kind: 'unusable'; message: string }
  /** Ready to record. */
  | { kind: 'ready' }
  /** Recording in progress (the seconds come from the recorder). */
  | { kind: 'recording' }
  /** Uploading and transcribing. */
  | { kind: 'working' }
  /** Done — here is what we heard. */
  | { kind: 'done'; transcript: string; confidence: number }
  /** Failed, but he can try again on the same link. */
  | { kind: 'retry'; message: string }
  /** Failed for a reason trying again will not fix. */
  | { kind: 'broken'; message: string }

/** Hard stop. Long enough for a considered answer, short enough to stay a note. */
const MAX_SECONDS = 90
/** Below this the transcript is probably wrong; say so rather than pretend. */
const LOW_CONFIDENCE = 0.6

export default function Record() {
  const { token = '' } = useParams<{ token: string }>()
  const [phase, setPhase] = useState<Phase>({ kind: 'checking' })

  const recorder = useWavRecorder({
    maxSeconds: MAX_SECONDS,
    onFinished: (wav) => void finished(wav),
  })

  // Check the link before showing a record button, so a dead link says so
  // immediately rather than after he has spoken.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        await checkVoiceLink(token)
        if (!cancelled) setPhase({ kind: 'ready' })
      } catch (e) {
        if (cancelled) return
        const message =
          e instanceof VoiceLinkError ? e.message : 'This link could not be opened.'
        setPhase({ kind: 'unusable', message })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  async function start() {
    const failure = await recorder.start()
    if (failure === null) {
      setPhase({ kind: 'recording' })
      return
    }
    // Name the cause (src/lib/microphone.ts): a page the site has not allowed
    // the microphone is not something he can fix in his browser.
    setPhase({ kind: 'retry', message: micFailureMessage(failure) })
  }

  async function finished(wav: ArrayBuffer) {
    // A near-empty buffer means the graph never captured anything — the exact
    // silent-failure class ScriptProcessorNode is chosen to avoid. Catch it here
    // rather than spending a transcription call to be told there were no words.
    if (wav.byteLength < 2048) {
      setPhase({
        kind: 'retry',
        message: 'That did not pick anything up. Hold the phone a bit closer and have another go.',
      })
      return
    }

    setPhase({ kind: 'working' })
    const outcome = await uploadRecording(token, new Blob([wav], { type: 'audio/wav' }))
    if (outcome.ok) {
      setPhase({
        kind: 'done',
        transcript: outcome.result.transcript,
        confidence: outcome.result.confidence,
      })
      return
    }
    setPhase(
      outcome.canRetry
        ? { kind: 'retry', message: outcome.message }
        : { kind: 'broken', message: outcome.message },
    )
  }

  return (
    <main className="min-h-[100svh] bg-surface-base px-5 py-8">
      <div className="mx-auto w-full max-w-md">
        <header className="mb-8">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-text-primary">
            Answer by voice
          </h1>
          <p className="mt-1 text-text-muted">
            Say it in your own words. You can check it before anything is saved.
          </p>
        </header>

        <div aria-live="polite">
          {phase.kind === 'checking' && <p className="text-text-muted">Checking your link…</p>}

          {phase.kind === 'unusable' && (
            <Notice tone="muted" title="This link will not open">
              {phase.message}
            </Notice>
          )}

          {(phase.kind === 'ready' || phase.kind === 'retry') && (
            <>
              {phase.kind === 'retry' && (
                <Notice tone="warn" title="Have another go">
                  {phase.message}
                </Notice>
              )}
              <BigButton onClick={() => void start()} label="Start recording">
                <Mic className="h-8 w-8" aria-hidden="true" />
              </BigButton>
              <p className="mt-4 text-center text-sm text-text-muted">
                Tap to start. Up to {MAX_SECONDS} seconds.
              </p>
            </>
          )}

          {phase.kind === 'recording' && (
            <>
              <BigButton onClick={() => recorder.stop()} label="Stop recording" recording>
                <Square className="h-7 w-7" aria-hidden="true" />
              </BigButton>
              <p className="mt-4 text-center font-display text-lg tabular-nums text-text-primary">
                {formatSeconds(recorder.seconds)}
              </p>
              <p className="mt-1 text-center text-sm text-text-muted">
                Recording — tap to stop when you are done.
              </p>
            </>
          )}

          {phase.kind === 'working' && (
            <div className="py-10 text-center">
              <p className="text-text-primary">Writing that up…</p>
              <p className="mt-1 text-sm text-text-muted">This takes a few seconds.</p>
            </div>
          )}

          {phase.kind === 'done' && (
            <div>
              <div className="mb-3 flex items-center gap-2 text-text-primary">
                <Check className="h-5 w-5" aria-hidden="true" />
                <p className="font-medium">Here is what I heard</p>
              </div>
              <blockquote className="rounded-card border border-border-subtle bg-surface-raised px-4 py-3 leading-relaxed text-text-primary">
                {phase.transcript}
              </blockquote>
              {phase.confidence < LOW_CONFIDENCE && (
                <p className="mt-2 text-sm text-text-muted">
                  Some of that was hard to make out — worth checking before you approve it.
                </p>
              )}
              {/* The loop is closed in the NEXT batch: he edits this and approves
                  it, which mints the fact and closes the gap. Saying so plainly
                  is better than a dead button or a promise the page cannot keep. */}
              <p className="mt-6 rounded-card border border-border-subtle bg-surface-subtle px-4 py-3 text-sm text-text-muted">
                Saved as a draft. Open your dashboard to check it over and approve it — nothing is
                published until you do.
              </p>
            </div>
          )}

          {phase.kind === 'broken' && (
            <Notice tone="danger" title="That did not save">
              {phase.message} Nothing has been lost — ask for a new link and try again.
            </Notice>
          )}
        </div>
      </div>
    </main>
  )
}

/* ---------- pieces ---------- */

function BigButton({
  onClick,
  label,
  recording = false,
  children,
}: {
  onClick: () => void
  label: string
  recording?: boolean
  children: React.ReactNode
}) {
  // Deliberately oversized: this is pressed with a work glove on, one-handed,
  // without looking. 44px is the accessibility minimum; this is far past it.
  return (
    <button
      onClick={onClick}
      aria-label={label}
      className={[
        'mx-auto flex h-32 w-32 items-center justify-center rounded-full text-white',
        'transition-transform active:[transform:scale(0.95)]',
        'focus-visible:outline-hidden focus-visible:ring-4 focus-visible:ring-brand-accent focus-visible:ring-offset-2',
        recording ? 'bg-text-danger' : 'bg-brand-primary',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

function Notice({
  tone,
  title,
  children,
}: {
  tone: 'muted' | 'warn' | 'danger'
  title: string
  children: React.ReactNode
}) {
  const border =
    tone === 'danger'
      ? 'border-[rgb(var(--color-text-danger)/0.4)]'
      : tone === 'warn'
        ? 'border-[rgb(var(--color-brand-accent)/0.5)]'
        : 'border-border-subtle'
  return (
    <div className={`mb-6 rounded-card border ${border} bg-surface-raised px-4 py-3`}>
      <p className="font-medium text-text-primary">{title}</p>
      <p className="mt-1 text-sm text-text-muted">{children}</p>
    </div>
  )
}

function formatSeconds(total: number): string {
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}
